import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";

export interface IAiPageContext {
    page: string;
    title: string;
    url: string;
    data?: Record<string, unknown>;
}

interface IResolvedContext {
    page: string;
    title: string;
    data?: Record<string, unknown>;
}

interface IPageAdapter {
    id: string;
    resolve: (props: Record<string, unknown>, path: string) => IResolvedContext;
}

const pick = (
    source: unknown,
    keys: string[],
): Record<string, unknown> | undefined => {
    if (typeof source !== "object" || source === null) {
        return undefined;
    }
    const record = source as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of keys) {
        if (record[key] !== undefined) {
            out[key] = record[key];
        }
    }
    return Object.keys(out).length > 0 ? out : undefined;
};

const compact = (value: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
        if (val !== null && val !== undefined) {
            out[key] = val;
        }
    }
    return out;
};

const decode = (segment: string | undefined): string | undefined => {
    if (segment === undefined || segment === "") {
        return undefined;
    }
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

// Resolves the report detail family (domain / source / ip / dmarc-management)
// from the URL segments so the assistant knows exactly what the user is viewing.
const resolveReportDetail = (
    props: Record<string, unknown>,
    path: string,
): IResolvedContext => {
    const segments = path.split("/").filter((s) => s !== "");
    const domain = decode(segments[1]);
    const kind = segments[2];
    const shared = { domain, dateRange: props.dateRange };

    if (kind === "ip") {
        return {
            page: "reports.ip",
            title: "Report detail – IP",
            data: {
                ...shared,
                source: decode(segments[3]),
                ip: decode(segments[4]),
            },
        };
    }
    if (kind === "source") {
        return {
            page: "reports.source",
            title: "Report detail – source",
            data: { ...shared, source: decode(segments[3]) },
        };
    }
    if (kind === "dmarc-management") {
        return {
            page: "reports.dmarc-management",
            title: "DMARC management",
            data: { domain },
        };
    }
    return {
        page: "reports.show",
        title: "Report detail",
        data: { ...shared, statistics: props.statistics },
    };
};

// Ordered by specificity: report detail (with a domain segment) is matched
// before the generic "/reports" index.
const ADAPTERS: IPageAdapter[] = [
    {
        id: "/dashboard",
        resolve: (p) => ({
            page: "dashboard",
            title: "Dashboard",
            data: { dateRange: p.dateRange, statistics: p.statistics },
        }),
    },
    {
        id: "/reports/",
        resolve: resolveReportDetail,
    },
    {
        id: "/reports",
        resolve: (p) => ({
            page: "reports",
            title: "Reports",
            data: {
                dateRange: p.dateRange,
                table: p.table,
                statistics: p.statistics,
            },
        }),
    },
    {
        id: "/domains",
        resolve: (p) => ({
            page: "domains",
            title: "Domains",
            data:
                pick(p, [
                    "totalDomainsCount",
                    "totalActiveDomains",
                    "totalInactiveDomains",
                    "totalParkedDomainsCount",
                    "totalEmailsReported",
                    "pagination",
                ]) ?? {},
        }),
    },
    {
        id: "/blacklists",
        resolve: (p) => ({
            page: "blacklists",
            title: "Blacklists",
            data: {
                ipsUsedCount: p.ipsUsedCount,
                ipsBlacklistedCount: p.ipsBlacklistedCount,
                table: p.table,
            },
        }),
    },
    {
        id: "/alerts/overview",
        resolve: (p) => ({
            page: "alerts",
            title: "Alerts overview",
            data: {
                notificationsEnabled: p.notificationsEnabled,
                table: p.table,
                pagination: p.pagination,
            },
        }),
    },
    {
        id: "/dns-tools",
        resolve: () => ({ page: "dns-tools", title: "DNS tools" }),
    },
    {
        id: "/integrations/mcp-server",
        resolve: () => ({ page: "mcp-server", title: "MCP server settings" }),
    },
];

export function useAiPageContext() {
    const page = usePage();

    const path = computed(() => page.url.split("?")[0]);

    const adapter = computed(
        () => ADAPTERS.find((a) => path.value.startsWith(a.id)) ?? null,
    );

    const available = computed(() => adapter.value !== null);

    const build = (): IAiPageContext | null => {
        if (adapter.value === null) {
            return null;
        }
        const props = page.props as Record<string, unknown>;
        const resolved = adapter.value.resolve(props, path.value);
        const data = compact(resolved.data ?? {});
        return {
            page: resolved.page,
            title: resolved.title,
            url: page.url,
            data: Object.keys(data).length > 0 ? data : undefined,
        };
    };

    const humanTitle = computed(() =>
        adapter.value === null
            ? ""
            : adapter.value.resolve(
                  page.props as Record<string, unknown>,
                  path.value,
              ).title,
    );

    return { available, humanTitle, build };
}
